import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import { BRAND } from '@/constants/site';

// Desktop primary nav items.
// "Testimonials" is intentionally NOT in this list — it lives as a
// sub-item under "Our Story" (renders as a hover dropdown on desktop,
// and as an indented item in the mobile sheet).
const LINKS = [
  { to: '/', label: 'Home', testId: NAV.home },
  { to: '/womens-design', label: "Women's Design", testId: NAV.womens },
  { to: '/mens-bespoke', label: "Men's Bespoke", testId: NAV.mens },
  {
    to: '/our-story',
    label: 'Our Story',
    testId: NAV.story,
    children: [
      { to: '/testimonials', label: 'Testimonials', testId: 'nav-testimonials' },
    ],
  },
  { to: '/contact', label: 'Contact', testId: NAV.contact },
  { to: '/sale', label: 'Sale', testId: 'nav-sale', highlight: true },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 left-0 right-0 z-40 transition-[background,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? 'bg-[hsl(var(--bb-charcoal))]/85 backdrop-blur-md border-b border-[hsl(var(--bb-text))]/8'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="bb-container flex items-center justify-between h-[120px] md:h-[160px]">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-center gap-3 group"
          aria-label={BRAND.name}
        >
          <img
            src="/assets/logo.png"
            alt="Bombay Bespoke Fashion House"
            className="h-24 md:h-32 w-auto select-none"
            draggable="false"
          />
          <span className="sr-only">{BRAND.name}</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9 ml-auto">
          {LINKS.map((l) => {
            if (l.children?.length) {
              return (
                <div key={l.to} className="relative group/menu">
                  <NavLink
                    to={l.to}
                    data-testid={l.testId}
                    className={({ isActive }) =>
                      `bb-link bb-link-reverse text-[hsl(var(--bb-text))] whitespace-nowrap inline-flex items-center gap-1 ${
                        isActive ? 'text-[hsl(var(--bb-gold))]' : ''
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown
                      size={12}
                      className="transition-transform duration-300 group-hover/menu:rotate-180"
                    />
                  </NavLink>
                  {/* Dropdown */}
                  <div className="absolute top-full left-1/2 -translate-x-1/2 pt-3 opacity-0 invisible translate-y-1 group-hover/menu:opacity-100 group-hover/menu:visible group-hover/menu:translate-y-0 transition-all duration-300">
                    <div className="bg-[hsl(var(--bb-charcoal))]/95 backdrop-blur-md border border-[hsl(var(--bb-text))]/10 min-w-[200px] py-3">
                      {l.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          data-testid={c.testId}
                          className={({ isActive }) =>
                            `block px-5 py-3 text-[11px] uppercase tracking-[0.22em] whitespace-nowrap transition-colors duration-300 hover:text-[hsl(var(--bb-gold))] ${
                              isActive
                                ? 'text-[hsl(var(--bb-gold))]'
                                : 'text-[hsl(var(--bb-text))]'
                            }`
                          }
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `bb-link bb-link-reverse whitespace-nowrap ${
                    l.highlight
                      ? 'text-[hsl(var(--bb-gold))] font-semibold'
                      : isActive
                      ? 'text-[hsl(var(--bb-gold))]'
                      : 'text-[hsl(var(--bb-text))]'
                  }`
                }
              >
                {l.label}
              </NavLink>
            );
          })}
          <Link
            to="/book-appointment"
            data-testid={NAV.appointment}
            className="inline-flex items-center px-5 py-3 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-medium border border-[hsl(var(--bb-charcoal))] hover:bg-transparent hover:text-[hsl(var(--bb-text))] transition-colors duration-500"
          >
            Book Appointment
          </Link>
        </nav>

        <button
          type="button"
          aria-label="Open menu"
          data-testid={NAV.mobileToggle}
          className="lg:hidden inline-flex items-center justify-center w-10 h-10 text-[hsl(var(--bb-text))]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile sheet */}
      <div
        data-testid={NAV.mobileSheet}
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-500 ${
          open ? 'max-h-[720px] opacity-100' : 'max-h-0 opacity-0'
        } bg-[hsl(var(--bb-ivory))] border-b border-[hsl(var(--bb-border))]`}
      >
        <div className="bb-container py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <div key={l.to}>
              <NavLink
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                end={l.to === '/'}
                className={({ isActive }) =>
                  `bb-display text-3xl tracking-tight ${
                    l.highlight
                      ? 'text-[hsl(var(--bb-gold))] italic'
                      : isActive
                      ? 'text-[hsl(var(--bb-gold))]'
                      : 'text-[hsl(var(--bb-text))]'
                  }`
                }
              >
                {l.label}
              </NavLink>
              {l.children?.map((c) => (
                <NavLink
                  key={c.to}
                  to={c.to}
                  data-testid={`${c.testId}-mobile`}
                  className={({ isActive }) =>
                    `block mt-3 ml-5 pl-4 border-l border-[hsl(var(--bb-gold))]/40 text-base uppercase tracking-[0.22em] ${
                      isActive
                        ? 'text-[hsl(var(--bb-gold))]'
                        : 'text-[hsl(var(--bb-text))]/80'
                    }`
                  }
                >
                  {c.label}
                </NavLink>
              ))}
            </div>
          ))}
          <Link
            to="/book-appointment"
            data-testid={`${NAV.appointment}-mobile`}
            className="mt-3 inline-flex items-center justify-center px-5 py-4 bg-[hsl(var(--bb-gold))] text-[hsl(var(--bb-charcoal))] uppercase tracking-[0.22em] text-[11px] font-medium"
          >
            Book Appointment
          </Link>
        </div>
      </div>
    </header>
  );
}
